import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { GET_STATISTICS_QUERY, PRODUCT_STATUS_COUNTS_QUERY } from './queries';
import {
  DateRangeInput,
  ProductStatusCounts,
  Statistics,
} from '@shopify-multiseller/graphql/partner/types';
import { Card, CardContent, Badge } from '../../components/primitives';
import { getPeriodText } from '../../helpers';
import { formatCents } from '../../helpers/formatters';
import { useShopCurrency } from '../../hooks';

type DashboardStatisticsProps = {
  dateRange: DateRangeInput;
};

type StatCardProps = {
  label: string;
  value: string;
  /** The badge beside the number — a change, or a state worth acting on. */
  chip?: React.ReactNode;
  /** What the number means: the comparison, and the way in. */
  context: React.ReactNode;
  testId?: string;
};

/**
 * A number, what it is being compared to, and where to go next.
 *
 * The icon chip that used to head each card said nothing the label didn't, and
 * a change badge with no baseline ("↑ 12.5%" against what?) is decoration.
 */
function StatCard({ label, value, chip, context, testId }: StatCardProps) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="text-2xl font-semibold tabular-nums tracking-tight text-foreground">
            {value}
          </span>
          {chip}
        </div>
        <p className="mt-1.5 text-xs text-muted-foreground">{context}</p>
      </CardContent>
    </Card>
  );
}

/**
 * "↑ 12.5%", as the design draws it.
 *
 * The arrow was a lucide icon, which put the direction in an <svg> and the
 * figure in a text node. That renders the same shape and reads the same to a
 * person, but it is not the same badge: the direction and the figure are one
 * fact and belong in one run, and split across two nodes nothing that reads the
 * page — a fidelity check, a screen reader, a text search — can see it whole.
 */
const ChangeBadge: React.FC<{ percent: number }> = ({ percent }) => (
  <Badge variant={percent < 0 ? 'destructive' : 'success'}>
    {`${percent < 0 ? '↓' : '↑'} ${Math.abs(percent)}%`}
  </Badge>
);

const statLink = 'font-medium text-primary hover:underline';

export const DashboardStatistics: React.FC<DashboardStatisticsProps> = ({ dateRange }) => {
  // Derived from the query directly; `data` goes undefined while a new
  // period is fetched, which re-shows the loader (same as before).
  const { data, error } = useQuery<{ statistics: Statistics }, { dateRange: DateRangeInput }>(
    GET_STATISTICS_QUERY,
    { variables: { dateRange }, fetchPolicy: 'cache-and-network' },
  );
  // The catalogue breakdown the products card needs: a total on its own
  // doesn't say whether anything is stuck waiting on the shop.
  const { data: countsData } = useQuery<{ productStatusCounts: ProductStatusCounts }>(
    PRODUCT_STATUS_COUNTS_QUERY,
  );
  const currencyCode = useShopCurrency();
  const stats = data?.statistics;
  const counts = countsData?.productStatusCounts;
  const period = getPeriodText(dateRange.periodType).toLowerCase();
  const window = period.replace(/^last\s+/, '');

  if (error) return <ErrorState />;
  if (!stats) return <Loader />;

  // The change is a percentage against the previous period, so the baseline it
  // was measured from can be stated rather than left implied.
  const previousOrders = Math.round(stats.orders.value / (1 + stats.orders.changePercent / 100));

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <StatCard
        testId="stat-orders"
        label={`Orders · ${period}`}
        value={Number(stats.orders.value).toLocaleString()}
        chip={<ChangeBadge percent={stats.orders.changePercent} />}
        context={
          <>
            {Number.isFinite(previousOrders)
              // Name the window, not "the previous period": the card's own
              // label already says "last 30 days", so the comparison should
              // say what it is being compared against in the same terms. The
              // leading "last" comes off — "the previous last 30 days" is not
              // English.
              ? `vs ${previousOrders.toLocaleString()} the previous ${window} · `
              : ''}
            <Link to="/orders" className={statLink}>
              View orders
            </Link>
          </>
        }
      />
      <StatCard
        testId="stat-earnings"
        label={`Earnings · ${period}`}
        value={formatCents({ amount: stats.earnings.value, currencyCode })}
        chip={<ChangeBadge percent={stats.earnings.changePercent} />}
        context={
          <>
            After fees &amp; commission ·{' '}
            <Link to="/payouts" className={statLink}>
              View payouts
            </Link>
          </>
        }
      />
      <StatCard
        testId="stat-products"
        label="Products"
        value={Number(stats.products).toLocaleString()}
        chip={
          counts && counts.awaitingReview > 0 ? (
            <Badge variant="warning">{`${counts.awaitingReview} awaiting approval`}</Badge>
          ) : undefined
        }
        context={
          <>
            {counts ? `${counts.active} live in the store · ` : ''}
            <Link to="/products" className={statLink}>
              View products
            </Link>
          </>
        }
      />
    </div>
  );
};

const ErrorState = () => (
  <Card>
    <CardContent className="p-4">
      <p className="text-sm font-medium text-foreground">These numbers didn&apos;t load</p>
      <p className="mt-1 text-xs text-muted-foreground">
        Reload the page to try again. Nothing is wrong with your orders or payouts.
      </p>
    </CardContent>
  </Card>
);

export const Loader: React.FC = () => (
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
    {[0, 1, 2].map((i) => (
      <Card key={i}>
        <CardContent className="p-4">
          <div className="h-3 w-24 animate-pulse rounded bg-muted" />
          <div className="mt-2 h-7 w-24 animate-pulse rounded bg-muted" />
          <div className="mt-2 h-3 w-36 animate-pulse rounded bg-muted" />
        </CardContent>
      </Card>
    ))}
  </div>
);
